// Invitations List Web Component
import { escapeHtml } from '../utils/html-utils.js';

class InvitationsList extends HTMLElement {
  constructor() {
    super();
    this.invitations = [];
    this.isLoading = true;
    this.isLoadingInvitations = false;
    this.currentUser = null;
  }

  connectedCallback() {
    this.render();
    this.attachEventListeners();
    this.loadInvitations();
  }

  isVisible() {
    // Check if the parent friends-section is visible
    const friendsSection = document.getElementById('friends-section');
    const sectionVisible = friendsSection && !friendsSection.classList.contains('hidden');
    return sectionVisible;
  }

  render() {
    this.innerHTML = `
      <div class="invitations-list">
        <div class="invitations-header">
          <h3>📨 Sent Invitations</h3>
          <button class="btn btn-sm btn-outline refresh-btn" id="refreshBtn">
            🔄 Refresh
          </button>
        </div>

        <div class="invitations-content">
          ${this.renderContent()}
        </div>
      </div>
    `;
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <p>Loading invitations...</p>
        </div>
      `;
    }

    // Filter out accepted invitations - they're now friends, no need to show them
    const pendingInvitations = (this.invitations || []).filter(inv =>
      inv.status !== 'accepted' && !inv.isExpired
    );

    if (pendingInvitations.length === 0) {
      return `
        <div class="empty-state">
          <span class="empty-state-icon">📨</span>
          <h4>No invitations sent</h4>
          <p>Invite friends to join Fun Old Times from the home page!</p>
        </div>
      `;
    }

    return `
      <div id="alertContainer"></div>
      ${pendingInvitations.map(inv => this.renderInvitation(inv)).join('')}
    `;
  }

  renderInvitation(invitation) {
    const { id, email, status, createdAt, lastSentAt, isExpired } = invitation;
    const statusClass = isExpired ? 'expired' : status;
    const displayStatus = isExpired ? 'expired' : status;

    const lastSentDate = new Date(lastSentAt);
    const now = new Date();
    const daysSinceLastSent = (now - lastSentDate) / (1000 * 60 * 60 * 24);

    // Check if current user is admin
    const isAdmin = this.currentUser && this.currentUser.role === 'admin';

    // Admins can always resend, regular users follow the 1-day rule
    const canResend = isAdmin || (status === 'pending' && !isExpired && daysSinceLastSent >= 1) || isExpired;

    const daysUntilResend = canResend ? 0 : Math.ceil(1 - daysSinceLastSent);

    return `
      <div class="invitation-item ${statusClass}" data-invitation-id="${id}">
        <div class="invitation-info">
          <div class="invitation-email">${escapeHtml(email)}</div>
          <div class="invitation-meta">
            Sent ${this.formatDate(createdAt)}
            ${lastSentAt && lastSentAt !== createdAt ? ` • Last reminder ${this.formatDate(lastSentAt)}` : ''}
          </div>
          <span class="invitation-status status-${displayStatus}">
            ${displayStatus === 'pending' ? '⏳ Pending' : ''}
            ${displayStatus === 'accepted' ? '✅ Accepted' : ''}
            ${displayStatus === 'expired' ? '⏰ Expired' : ''}
          </span>
        </div>
        <div class="invitation-actions">
          ${(status === 'pending' || isExpired) ? `
            <button 
              class="resend-btn ${isAdmin ? 'admin-resend' : ''}" 
              data-invitation-id="${id}"
              ${!canResend ? 'disabled' : ''}
              title="${!canResend ? `Can resend in ${daysUntilResend} day${daysUntilResend !== 1 ? 's' : ''}` : (isAdmin ? 'Admin: Resend invitation email (no throttling)' : 'Resend invitation email')}"
            >
              ${canResend ? (isExpired ? '🔄 Resend' : (isAdmin ? '⚡ Resend' : '📧 Resend')) : `🕐 ${daysUntilResend}d`}
            </button>
          ` : ''}
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    // Refresh button
    const refreshBtn = this.querySelector('#refreshBtn');
    refreshBtn?.addEventListener('click', () => {
      this.loadInvitations();
    });

    // Resend buttons
    this.addEventListener('click', (e) => {
      if (e.target.closest('.resend-btn')) {
        const btn = e.target.closest('.resend-btn');
        const invitationId = btn.dataset.invitationId;
        if (!btn.disabled) {
          this.resendInvitation(invitationId, btn);
        }
      }
    });

    // Listen for section shown events to load invitations when Friends section is displayed
    this.handleSectionShown = (event) => {
      if (event.detail.section === 'friends') {
        // Only load invitations if we have a current user and API is available
        if (this.currentUser && window.API && this.isAuthenticated()) {
          this.loadInvitations();
        }
      }
    };
    document.addEventListener('section-shown', this.handleSectionShown);

    // Listen for invitation events
    this.handleInvitationSent = () => {
      // Refresh to show new invitation
      setTimeout(() => this.loadInvitations(), 500);
    };
    document.addEventListener('invitation-sent', this.handleInvitationSent);

    // Listen for invitation accepted events to refresh the list
    this.handleInvitationAccepted = () => {
      // Refresh to remove accepted invitation from list
      setTimeout(() => this.loadInvitations(), 500);
    };
    document.addEventListener('invitation-accepted', this.handleInvitationAccepted);
  }

  async loadInvitations() {
    // Prevent multiple simultaneous calls
    if (this.isLoadingInvitations) {
      return;
    }

    this.isLoadingInvitations = true;
    this.isLoading = true;
    this.updateContent();

    try {
      const response = await window.API.request('/invitations');
      this.invitations = response.invitations || [];
    } catch (error) {
      window.logger.error('Failed to load invitations:', error);
      this.invitations = [];
      this.showError('Failed to load invitations');
    } finally {
      this.isLoading = false;
      this.isLoadingInvitations = false;
      this.updateContent();
    }
  }

  isAuthenticated() {
    return this.currentUser && this.currentUser.id;
  }

  showError(message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'alert alert-error';
    errorDiv.textContent = message;
    errorDiv.style.position = 'fixed';
    errorDiv.style.top = '20px';
    errorDiv.style.right = '20px';
    errorDiv.style.zIndex = '10000';
    errorDiv.style.animation = 'fadeIn 0.3s ease-in-out';

    document.body.appendChild(errorDiv);

    setTimeout(() => errorDiv.remove(), 4000);
  }

  updateContent() {
    const contentContainer = this.querySelector('.invitations-content');
    if (contentContainer) {
      contentContainer.innerHTML = this.renderContent();
      this.attachEventListeners();
    }
  }

  async resendInvitation(invitationId, btn) {
    const originalContent = btn.innerHTML;

    btn.disabled = true;
    btn.innerHTML = '⏳ Sending...';

    try {
      const response = await window.API.request(`/invitations/resend/${invitationId}`, {
        method: 'POST'
      });

      // Show success message
      this.showAlert(response.message, 'success');

      // Refresh the invitations
      await this.loadInvitations();

    } catch (error) {
      window.logger.error('Failed to resend invitation:', error);
      btn.disabled = false;
      btn.innerHTML = originalContent;
      this.showAlert(error.message || 'Failed to resend invitation', 'error');
    }
  }

  formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      if (days < 7) {
        return `${days}d ago`;
      } else {
        return date.toLocaleDateString();
      }
    }
  }

  showAlert(message, type = 'success') {
    const alertContainer = this.querySelector('#alertContainer');
    if (!alertContainer) return;

    const alert = document.createElement('div');
    alert.className = `alert alert-${type}`;
    alert.textContent = message;

    alertContainer.innerHTML = '';
    alertContainer.appendChild(alert);

    // Auto-dismiss after 4 seconds
    setTimeout(() => {
      alert.remove();
    }, 4000);
  }


  // Public method to refresh invitations
  refresh() {
    this.loadInvitations();
  }

  disconnectedCallback() {
    // Clean up event listeners to prevent memory leaks
    if (this.handleSectionShown) {
      document.removeEventListener('section-shown', this.handleSectionShown);
    }
    if (this.handleInvitationSent) {
      document.removeEventListener('invitation-sent', this.handleInvitationSent);
    }
    if (this.handleInvitationAccepted) {
      document.removeEventListener('invitation-accepted', this.handleInvitationAccepted);
    }
  }
}

// Register the custom element
customElements.define('invitations-list', InvitationsList);

